import { useMenuContext } from '#pplx/frontend/aether/components/internal/menu/useMenuContext.js';
import { useCallback, useEffect, useId, useRef } from 'react';
/**
 * Hook for managing a registry of menu items.
 *
 * This hook maintains a map of menu item IDs to their imperative handles,
 * allowing access to all menu items within a menu for programmatic focus management.
 *
 * @example
 * ```tsx
 * const menuItems = useMenuItemRegistry();
 *
 * // Register a menu item
 * menuItems.register('item-1', { focus: () => itemRef.current?.focus() });
 *
 * // Get all items
 * const items = menuItems.getAll();
 * items[0]?.focus();
 *
 * // Unregister when unmounting
 * menuItems.unregister('item-1');
 * ```
 */
export function useMenuItemRegistry() {
    const registryRef = useRef(new Map());
    const register = useCallback((id, handle) => {
        registryRef.current.set(id, handle);
    }, []);
    const unregister = useCallback((id) => {
        registryRef.current.delete(id);
    }, []);
    const focusFirstMenuItem = useCallback(() => {
        const items = Array.from(registryRef.current.values());
        items[0]?.focus();
    }, []);
    return {
        register,
        unregister,
        focusFirstMenuItem,
    };
}
/**
 * Hook for registering a menu item with its parent menu context.
 *
 * This hook automatically:
 * - Generates a unique ID for the menu item
 * - Retrieves the parent menu context
 * - Registers the menu item's imperative handle with the parent menu's registry on mount
 * - Unregisters it on unmount
 *
 * This allows menu consumers to access all menu items via the useMenu hook.
 *
 * @param handle - Imperative handle for the menu item
 *
 * @example
 * ```tsx
 * function MenuItemComponent() {
 *   const itemRef = useRef<HTMLDivElement>(null);
 *
 *   const focus = useCallback(() => {
 *     itemRef.current?.focus();
 *   }, []);
 *
 *   useMenuItemRegistration({ focus });
 *
 *   return <div ref={itemRef}>Menu Item</div>;
 * }
 * ```
 */
export function useMenuItemRegistration(handle) {
    const context = useMenuContext();
    const itemId = useId();
    useEffect(() => {
        if (!context)
            return;
        context.menuItems.register(itemId, handle);
        return () => {
            context.menuItems.unregister(itemId);
        };
    }, [context, itemId, handle]);
}
//# sourceMappingURL=useMenuItemRegistry.js.map